import type { AssetType } from '@skytech/types'

import type { ProductExtended, ProductGroupDataExtended } from '@/modules/ecom-policy/types'

export type PolicyProductCardProps = {
  id: string | number // groupKey for groups
  name: string // groupName for groups
  price: number // priceFrom for groups
  description: string | null // desc for single product
  imgUrl: string | null // imageUrl for groups
  currency: string | null
  vatPct: number
  inStock: string | null
  isSelected: boolean
  isExpired: boolean
  isMandatory: boolean
  hasAccessories: boolean

  // Optional for single product
  priceId?: number | null
  quantityLimit?: number
  alias?: string | null
  aliasDescription?: string | null
  assetType?: AssetType | null
  code?: string
  sku?: string | null
  manufacturerProductNo?: string
  manufacturerName?: string | null
  webUrl?: string | null

  // Optional for grouped products
  groupedProducts?: ProductExtended[]
  category?: string

  isGrouped: boolean // Flag to differentiate between grouped and single product
}

export type SingleProduct = Omit<ProductExtended, 'isAccessory' | 'category' | 'catId'>

export const normalizeProductData = ({
  group,
  singleProduct,
  hasAccessories,
}: {
  group?: ProductGroupDataExtended
  singleProduct?: SingleProduct
  hasAccessories?: boolean
}): PolicyProductCardProps => {
  if (group) {
    return {
      id: group.groupKey,
      name: group.groupName,
      price: group.priceFrom,
      description: group.description,
      imgUrl: group.imageUrl,
      currency: group.currency,
      vatPct: group.vatPct,
      inStock: group.inStock,
      isSelected: group.isSelected,
      isExpired: Boolean(group.isExpired),
      isMandatory: group.isMandatory,
      groupedProducts: group.groupedProducts,
      category: group.category,
      hasAccessories: group.hasAccessories,
      isGrouped: true,
    }
  }

  if (singleProduct) {
    return {
      id: singleProduct.id,
      name: singleProduct.name,
      price: singleProduct.price,
      description: singleProduct.desc,
      imgUrl: singleProduct.imgUrl,
      currency: singleProduct.currency,
      vatPct: singleProduct.vatPct,
      inStock: singleProduct.inStock,
      isSelected: Boolean(singleProduct.isSelected),
      isMandatory: Boolean(singleProduct.isMandatory),
      isExpired: Boolean(singleProduct.isExpired),
      priceId: singleProduct.priceId,
      quantityLimit: singleProduct.quantityLimit,
      alias: singleProduct.alias,
      aliasDescription: singleProduct.aliasDescription,
      assetType: singleProduct.assetType,
      code: singleProduct.code,
      sku: singleProduct.sku,
      manufacturerProductNo: singleProduct.manufacturerProductNo,
      manufacturerName: singleProduct.manufacturerName,
      webUrl: singleProduct.webUrl,
      hasAccessories: hasAccessories || false,
      isGrouped: false,
    }
  }

  throw new Error('Either group or single product must be provided')
}
